import React from 'react';
import { Link } from 'react-router-dom';

// Transparency block for the homepage: upload guidelines, digital fulfillment,
// and trust/compliance promises. Designed to make the service clear to customers
// (and reviewers) within seconds. Content is intentionally honest about AI results.
const TrustProcess = () => {
  const trustPoints = [
    'We do not sell your uploaded photos.',
    'Your photos are used only to create your headshots.',
    'You own your final generated headshots.',
    'You can request deletion of your data at any time.',
    'Uploaded photos are deleted within 30 days.',
    'Generated headshots are deleted within 30 days.',
  ];

  return (
    <section className="bg-black text-white py-16 px-[15px]">
      <div className="mx-auto max-w-[1100px] space-y-16">

        {/* Photo upload guidelines */}
        <div>
          <div className="text-center mb-8">
            <span className="inline-block text-xs font-semibold tracking-wide uppercase text-cyan-400 mb-2">
              Get the best results
            </span>
            <h2 className="text-3xl font-bold">Photo upload guidelines</h2>
            <p className="text-white/70 mt-2 max-w-[680px] mx-auto">
              Better input photos produce better AI headshots. Follow these tips for the best results.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="bg-neutral-900 rounded-2xl p-6">
              <h3 className="font-semibold text-lg mb-3 text-green-400">✓ Do upload</h3>
              <ul className="space-y-2 text-white/80 list-disc list-inside">
                <li>Clear, well-lit, in-focus photos.</li>
                <li>A variety of angles, expressions, and backgrounds.</li>
                <li>Recent photos that look like you today.</li>
                <li>Photos where your full face is clearly visible.</li>
              </ul>
            </div>
            <div className="bg-neutral-900 rounded-2xl p-6">
              <h3 className="font-semibold text-lg mb-3 text-red-400">✕ Please avoid</h3>
              <ul className="space-y-2 text-white/80 list-disc list-inside">
                <li>Sunglasses, hats, or anything covering your face.</li>
                <li>Heavy filters or beauty effects.</li>
                <li>Group photos — upload only photos of yourself.</li>
                <li>Blurry, dark, low-resolution, or extreme-angle images.</li>
              </ul>
            </div>
          </div>
          <p className="text-center mt-4">
            <Link to="/ai-headshot-upload-guide" className="text-cyan-400 underline text-sm">
              Read the full upload guide →
            </Link>
          </p>
        </div>

        {/* Fulfillment / delivery */}
        <div className="bg-neutral-900 rounded-2xl p-8">
          <div className="text-center mb-6">
            <span className="inline-block text-xs font-semibold tracking-wide uppercase text-cyan-400 mb-2">
              Digital delivery
            </span>
            <h2 className="text-3xl font-bold">How your headshots are delivered</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-3 text-center">
            <div>
              <div className="text-2xl mb-2">💻</div>
              <h3 className="font-semibold mb-1">Digital product — no shipping</h3>
              <p className="text-white/70 text-sm">There is nothing to ship. Your headshots are delivered electronically.</p>
            </div>
            <div>
              <div className="text-2xl mb-2">⏱️</div>
              <h3 className="font-semibold mb-1">Ready in about an hour</h3>
              <p className="text-white/70 text-sm">Most packages are delivered to your dashboard within ~1–2 hours. Times may vary.</p>
            </div>
            <div>
              <div className="text-2xl mb-2">⬇️</div>
              <h3 className="font-semibold mb-1">Download from your dashboard</h3>
              <p className="text-white/70 text-sm">Preview and download your finished headshots anytime from your account.</p>
            </div>
          </div>
          <p className="text-center text-white/50 text-sm mt-6">
            Results are AI-generated and may vary with the quality of your uploaded photos. If you don&rsquo;t get a
            usable headshot, we&rsquo;ll re-run your generation or refund you — see our{' '}
            <Link to="/delivery" className="underline">Delivery Policy</Link> and{' '}
            <Link to="/refund" className="underline">Refund Policy</Link>.
          </p>
        </div>

        {/* Trust / compliance band */}
        <div>
          <div className="text-center mb-8">
            <span className="inline-block text-xs font-semibold tracking-wide uppercase text-cyan-400 mb-2">
              Your privacy &amp; trust
            </span>
            <h2 className="text-3xl font-bold">We respect your photos and your privacy</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {trustPoints.map((point, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-neutral-900 rounded-xl p-4">
                <span className="text-cyan-400 mt-0.5">✓</span>
                <span className="text-white/85 text-sm">{point}</span>
              </div>
            ))}
          </div>
          <div className="text-center mt-8 bg-cyan-950/40 border border-cyan-800/50 rounded-2xl p-6">
            <h3 className="text-xl font-bold mb-2">🛡️ Usable-headshot guarantee</h3>
            <p className="text-white/80 max-w-[640px] mx-auto">
              If you don&rsquo;t receive at least one usable professional headshot, contact us within 14 days. We&rsquo;ll
              refund you when no final images have been downloaded and your request meets our refund conditions.
            </p>
            <p className="text-sm text-white/60 mt-4">
              <Link to="/legal" className="underline">Terms &amp; Privacy</Link>{' · '}
              <Link to="/refund" className="underline">Refund</Link>{' · '}
              <Link to="/delivery" className="underline">Delivery</Link>{' · '}
              <Link to="/acceptable-use" className="underline">Acceptable Use</Link>{' · '}
              <Link to="/ai-headshot-privacy-data-deletion" className="underline">Privacy &amp; Data Deletion</Link>
            </p>
          </div>
        </div>

      </div>
    </section>
  );
};

export default TrustProcess;
